import React from "react";
import { KTMScreen } from "ktm-ui";
import toastr from "toastr";

const readJson = async (response: Response) => {
  if (!response.ok) {
    throw new Error(await response.text());
  }
  return response.json();
};

const escapeHtml = (text: string) =>
  text.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

const todayStamp = () => {
  const date = new Date();
  return (
    date.getFullYear() +
    "_" +
    ("0" + (date.getMonth() + 1)).slice(-2) +
    "_" +
    ("0" + date.getDate()).slice(-2)
  );
};

const triggerDownload = (href: string, fileName: string) => {
  const a = document.createElement("a");
  a.href = href;
  a.download = fileName;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  a.remove();
};

export default KTMScreen(
  { name: "paymentfiles" },
  class extends React.Component<any> {
    state = {
      paymentBatches: [],
      skip: 0,
      loading: false,
      adjudicated: 0,
      unpaidEft: 0,
      unpaidChq: 0,
      settlingBatchId: null,
      downloadingBatchId: null,
    };

    isBusy = () =>
      this.state.loading ||
      this.state.settlingBatchId !== null ||
      this.state.downloadingBatchId !== null;

    componentDidMount() {
      this.refresh();
    }

    refresh = () => {
      this.setState({ loading: true });

      const counts = fetch("/api/Payment/getpendingpayments", { method: "GET" })
        .then((response) => response.json())
        .then((data) => {
          this.setState({
            adjudicated: data.adjudicated,
            unpaidEft: data.unpaidEft,
            unpaidChq: data.unpaidChq,
          });
        });

      const batches = fetch("/api/Payment/getbatch?skip=0", { method: "GET" })
        .then((response) => response.json())
        .then((data) => {
          this.setState({ paymentBatches: data, skip: 10 });
        });

      return Promise.all([counts, batches])
        .then(() => {
          this.setState({ loading: false });
        })
        .catch(() => {
          this.setState({ loading: false });
        });
    };

    loadMore = () => {
      if (this.isBusy()) {
        return;
      }
      this.setState({ loading: true });
      fetch("/api/Payment/getbatch?skip=" + this.state.skip, { method: "GET" })
        .then((response) => response.json())
        .then((data) => {
          this.setState({
            paymentBatches: [...this.state.paymentBatches, ...data],
            skip: this.state.skip + 10,
            loading: false,
          });
        })
        .catch(() => {
          this.setState({ loading: false });
        });
    };

    runAction = (url, options, successMessage, failureMessage) => {
      if (this.isBusy()) {
        return Promise.resolve();
      }
      this.setState({ loading: true });
      return fetch(url, options)
        .then(readJson)
        .then(() => this.refresh())
        .then(() => {
          toastr.success(successMessage, "Success");
        })
        .catch(() => {
          this.setState({ loading: false });
          toastr.error(failureMessage, "Error");
        });
    };

    runPayment = () =>
      this.runAction(
        "/api/Payment/run",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({}),
        },
        "Payment process complete",
        "Payment process failed"
      );

    generateEftBatch = () =>
      this.runAction(
        "/api/Payment/generateeftbatch",
        { method: "GET" },
        "EFT payment batch generated",
        "Failed to generate EFT batch"
      );

    generateChqBatch = () =>
      this.runAction(
        "/api/Payment/generatechqbatch",
        { method: "GET" },
        "CHQ payment batch generated",
        "Failed to generate CHQ batch"
      );

    payBatch = (route, id, label) => {
      if (this.isBusy()) {
        return;
      }
      this.setState({ settlingBatchId: id, loading: true });
      fetch("/api/Payment/" + route + "/" + id, { method: "POST" })
        .then(readJson)
        .then(() => this.refresh())
        .then(() => {
          toastr.success(label + " batch finalized", "Success");
        })
        .catch((error) => {
          this.setState({ loading: false });
          toastr.error("Failed to finalize " + label + " batch: " + escapeHtml(error.message), "Error");
        })
        .finally(() => {
          this.setState({ settlingBatchId: null });
        });
    };

    payEftBatch = (id) => {
      this.payBatch("payeftbatch", id, "EFT");
    };

    payChqBatch = (id) => {
      this.payBatch("paychqbatch", id, "CHQ");
    };

    getEftFile = (id) => {
      if (this.isBusy()) {
        return;
      }
      this.setState({ downloadingBatchId: id });
      fetch("/api/Payment/geteftbatchfile/" + id, { method: "GET" })
        .then(readJson)
        .then((data) => {
          triggerDownload(
            "data:text/plain;charset=utf-8," + encodeURIComponent(data.textFile),
            data.fileName
          );
          toastr.success("EFT file downloaded", "Success");
        })
        .catch((error) => {
          console.error("Error downloading EFT file", error);
          toastr.error("Failed to download EFT file", "Error");
        })
        .finally(() => {
          this.setState({ downloadingBatchId: null });
        });
    };

    getChqFile = (id) => {
      if (this.isBusy()) {
        return;
      }
      this.setState({ downloadingBatchId: id });
      fetch("/api/Payment/getchqbatchfile/" + id, { method: "GET" })
        .then(async (response) => {
          if (!response.ok) {
            throw new Error(await response.text());
          }
          return response.blob();
        })
        .then((blob) => {
          triggerDownload(window.URL.createObjectURL(blob), "CHQ_" + todayStamp() + ".zip");
          toastr.success("CHQ file downloaded", "Success");
        })
        .catch((error) => {
          console.error("Error downloading CHQ file", error);
          toastr.error("Failed to download CHQ file", "Error");
        })
        .finally(() => {
          this.setState({ downloadingBatchId: null });
        });
    };

    renderMarkPaidButton(data) {
      if (data.isPaid) {
        return (
          <td>
            <button type="button" className="btn btn-sm btn-outline-danger" disabled>
              Paid
            </button>
          </td>
        );
      }

      const onClick =
        data.paymentMethod == "EFT"
          ? () => this.payEftBatch(data.id)
          : data.paymentMethod == "CHEQUE"
          ? () => this.payChqBatch(data.id)
          : null;

      if (onClick == null) {
        return null;
      }

      return (
        <td>
          <button
            onClick={onClick}
            disabled={this.isBusy()}
            type="button"
            className="btn btn-sm btn-danger"
          >
            Finalize Batch
          </button>
        </td>
      );
    }

    renderDownloadButton(data) {
      if (!data.isPaid) {
        return <td></td>;
      }

      if (data.paymentMethod == "EFT") {
        return (
          <td>
            <button
              onClick={() => this.getEftFile(data.id)}
              disabled={this.isBusy()}
              type="button"
              className="btn btn-sm btn-outline-primary pmt-file-dl-btn"
            >
              Download EFT
            </button>
          </td>
        );
      }

      if (data.paymentMethod == "CHEQUE") {
        return (
          <td>
            <button
              onClick={() => this.getChqFile(data.id)}
              disabled={this.isBusy()}
              type="button"
              className="btn btn-sm btn-outline-primary pmt-file-dl-btn"
            >
              Download CHQ
            </button>
          </td>
        );
      }

      return <td></td>;
    }

    render() {
      const formatter = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      });

      const batchRows = this.state.paymentBatches.map((data, i) => (
        <tr key={i}>
          <td>{data.id}</td>
          <td>{data.eftfileSeq}</td>
          {data.isPaid ? (
            <td style={{ color: "green" }}>Yes</td>
          ) : (
            <td style={{ color: "red" }}>No</td>
          )}
          <td>{data.paymentDate}</td>
          <td>{data.paymentMethod}</td>
          <td className="text-end">{formatter.format(data.paymentTotal || 0)}</td>
          <td>{data.createdAt}</td>
          {this.renderMarkPaidButton(data)}
          {this.renderDownloadButton(data)}
        </tr>
      ));

      const batchTable = (
        <div style={{ maxHeight: "500px", overflowY: "scroll" }}>
          <table className="table">
            <thead>
              <tr>
                <th>Id</th>
                <th>File Sequence</th>
                <th>Paid</th>
                <th>Payment Date</th>
                <th>Payment Method</th>
                <th className="text-end">Payment Total</th>
                <th>Created</th>
                <th></th>
                <th></th>
              </tr>
            </thead>
            <tbody>{batchRows}</tbody>
          </table>
        </div>
      );

      const paymentActionButtons = (
        <div className="btn-group mb-3">
          <button
            onClick={() => this.runPayment()}
            type="button"
            className="btn btn-sm btn-danger"
            disabled={this.isBusy()}
          >
            Run Payment Process
          </button>
          <button
            onClick={() => this.generateEftBatch()}
            type="button"
            className="btn btn-sm btn-outline-danger"
            disabled={this.isBusy()}
          >
            Generate EFT
          </button>
          <button
            onClick={() => this.generateChqBatch()}
            type="button"
            className="btn btn-sm btn-outline-danger"
            disabled={this.isBusy()}
          >
            Generate CHQ
          </button>
        </div>
      );

      const spinner = (
        <div
          style={{
            width: "100%",
            height: 300,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div className="spinner-border text-primary" role="status"></div>
        </div>
      );

      return (
        <div className="p-4">
          {!this.state.loading && (
            <div className="card p-3 alert alert-success" style={{ marginBottom: 15 }}>
              <ul>
                <li>
                  Adjudicated claims pending payment processing:{" "}
                  <b>{this.state.adjudicated}</b>
                </li>
                <li>
                  Unpaid EFT pending generation: <b>{this.state.unpaidEft}</b>
                </li>
                <li>
                  Unpaid CHQ pending generation: <b>{this.state.unpaidChq}</b>
                </li>
              </ul>
            </div>
          )}
          <div>{paymentActionButtons}</div>
          {this.state.loading ? spinner : batchTable}
          <button
            className="btn btn-sm btn-primary"
            onClick={() => this.loadMore()}
            disabled={this.isBusy()}
          >
            Load More
          </button>
        </div>
      );
    }
  }
);
